import React, { useEffect, useState } from 'react';
import s from './PromtBlocks.module.css';
import { commitHistory, updateSelectedImageSetting } from '../../../../store/slices/imageSlice';
import { useSelector } from 'react-redux';
import { selectSelectedImage } from '../../../../store/selectors/imageSelectors';
import { useAppDispatch } from '../../../../store/store';
import Slider from '../../../../shared/ui/slider/Slider';

export type EffectDef = {
    name: string;
    color: string;
    gradient?: string;
};

const SERIES: Array<{ title: string; effects: EffectDef[] }> = [
    {
        title: 'Warm series',
        effects: [
            { name: 'Tokyo', color: '#FF0505' },
            { name: 'Malibu', color: '#FF2323' },
            { name: 'Miami', color: '#FC4343' },
            { name: 'Sahara', color: '#FF8C8C' },
            { name: 'Rio', color: '#FF6200' },
            { name: 'Phoenix', color: '#FF914D' },
            { name: 'Jerusalem', color: '#FFCD68' },
            { name: 'Havana', color: '#FFEB68' },
        ],
    },
    {
        title: 'Cold series',
        effects: [
            { name: 'Arctic', color: '#053BFF' },
            { name: 'Silberian', color: '#2348FF' },
            { name: 'Oslo', color: '#4378FC' },
            { name: 'Icelandic', color: '#8CABFF' },
            { name: 'Aurora', color: '#00B7FF' },
            { name: 'Glacier', color: '#4DD2FF' },
            { name: 'Alpine', color: '#68E6FF' },
            { name: 'Antarctic', color: '#B7FBF6' },
        ],
    },
    {
        title: 'Nature series',
        effects: [
            { name: 'Grove', color: '#249707' },
            { name: 'Amazon', color: '#165209' },
            { name: 'Hallow', color: '#32EF2F' },
            { name: 'Cedar', color: '#9EE326' },
            { name: 'Rainforest', color: '#ABE842' },
            { name: 'Safari', color: '#A57B28' },
            { name: 'Shade', color: '#BA8825' },
            { name: 'Desert', color: '#F9DA60' },
        ],
    },
    {
        title: 'Night series',
        effects: [
            {
                name: 'NY',
                color: 'linear-gradient(159deg, #FC0087 0%, #090979 53%, #000000 100%)',
                gradient: 'linear-gradient(159deg, #FC0087A3 0%, #09097951 53%, #0000004D 100%)',
            },
            {
                name: 'Moscow',
                color: 'linear-gradient(134deg, #CB67E1 0%, #000000 100%)',
                gradient: 'linear-gradient(134deg, #CB67E138 0%, #00000075 100%)',
            },
            {
                name: 'Dubai',
                color: 'linear-gradient(136deg, #8116FB 18.7%, #000000 100%)',
                gradient: 'linear-gradient(136deg, #8116FB7D 18.7%, #00000080 100%)',
            },
            {
                name: 'London',
                color: 'linear-gradient(126deg, #4016FB 38.19%, #000000 100%)',
                gradient: 'linear-gradient(126deg, #4016FB5E 38.19%, #00000078 100%)',
            },
            {
                name: 'Frankfurt',
                color: 'linear-gradient(126deg, #1E5BF4 38.19%, #000000 100%)',
                gradient: 'linear-gradient(126deg, #1E5BF459 38.19%, #00000061 100%)',
            },
            {
                name: 'Toronto',
                color: 'linear-gradient(126deg, #1EBBF4 38.19%, #000000 100%)',
                gradient: 'linear-gradient(126deg, #1EBBF468 38.19%, #0000007D 100%)',
            },
            {
                name: 'Seoul',
                color: 'linear-gradient(126deg, #F009B2 38.19%, #000000 100%)',
                gradient: 'linear-gradient(126deg, #F009B26B 38.19%, #0000004D 100%)',
            },
            {
                name: 'Shanghai',
                color: 'linear-gradient(126deg, #25F270 38.19%, #000000 100%)',
                gradient: 'linear-gradient(126deg, #25F27047 38.19%, #00000068 100%)',
            },
        ],
    },
    {
        title: 'Enigma series',
        effects: [
            {
                name: 'Secret',
                color: 'linear-gradient(135deg, #004CFF 0%, #FFFFFF 50%, #FF0004 100%)',
                gradient: 'linear-gradient(135deg, #1553E480 0%, #FFFFFF80 50%, #FF000480 100%)',
            },
            {
                name: 'Whisper',
                color: 'linear-gradient(135deg, #15E420 0%, #F4FFFD 50%, #1D57F7 100%)',
                gradient: 'linear-gradient(135deg, #15E42080 0%, #F4FFFD80 50%, #1D57F780 100%)',
            },
            {
                name: 'Cipher',
                color: 'linear-gradient(135deg, #CE12E7 0%, #F5F2FF 50%, #45D1F4 100%)',
                gradient: 'linear-gradient(135deg, #CE12E780 0%, #F5F2FF80 50%, #45D1F480 100%)',
            },
            {
                name: 'Riddle',
                color: 'linear-gradient(135deg, #DFF91C 0%, #FFFFFF 50%, #16F4AE 100%)',
                gradient: 'linear-gradient(135deg, #DFF91C80 0%, #FFFFFF80 50%, #16F4AE80 100%)',
            },
            {
                name: 'Labyrinth',
                color: 'linear-gradient(135deg, #165209 0%, #FFFFFF 50%, #EE1313 100%)',
                gradient: 'linear-gradient(135deg, #16520980 0%, #FFFFFF80 50%, #EE131380 100%)',
            },
            {
                name: 'Relic',
                color: 'linear-gradient(135deg, #13C6F8 0%, #FFFFFF 50%, #F89A0E 100%)',
                gradient: 'linear-gradient(135deg, #13C6F880 0%, #FFFFFF80 50%, #F89A0E80 100%)',
            },
            {
                name: 'Oracle',
                color: 'linear-gradient(135deg, #EC17F7 0%, #FFFFFF 50%, #100F0E 100%)',
                gradient: 'linear-gradient(135deg, #EC17F780 0%, #FFFFFF80 50%, #100F0E80 100%)',
            },
            {
                name: 'Cryptic',
                color: 'linear-gradient(135deg, #383734 0%, #FFFFFF 50%, #14C8FF 100%)',
                gradient: 'linear-gradient(135deg, #38373480 0%, #FFFFFF80 50%, #14C8FF80 100%)',
            },
        ],
    },
    {
        title: 'Ancient series',
        effects: [
            {
                name: 'Babylon',
                color: 'linear-gradient(135deg, #E48715 0%, #928F8F 100%)',
                gradient: 'linear-gradient(135deg, #E4871580 0%, #928F8F80 100%)',
            },
            {
                name: 'Rome',
                color: 'linear-gradient(135deg, #E48715 0%, #928F8F 100%)',
                gradient: 'linear-gradient(135deg, #E4871580 0%, #928F8F80 100%)',
            },
            {
                name: 'Spartan',
                color: 'linear-gradient(135deg, #FCB531 0%, #DCDBDB 100%)',
                gradient: 'linear-gradient(135deg, #FCB53180 0%, #DCDBDB80 100%)',
            },
            {
                name: 'Mayan',
                color: 'linear-gradient(135deg, #F5E691 0%, #FFFEFE 100%)',
                gradient: 'linear-gradient(135deg, #F5E69180 0%, #FFFEFE80 100%)',
            },
            {
                name: 'Atlantis',
                color: 'linear-gradient(135deg, #F5E691 0%, #FFFEFE 100%)',
                gradient: 'linear-gradient(135deg, #F5E69180 0%, #FFFEFE80 100%)',
            },
            {
                name: 'Egypt',
                color: 'linear-gradient(132deg, #F8D613 16.67%, #FFF 54.28%, #F89A0E 91.9%)',
                gradient:
                    'linear-gradient(132deg, #F8D61380 16.67%, #FFF80 54.28%, #F89A0E80 91.9%)',
            },
            {
                name: 'Persian',
                color: 'linear-gradient(132deg, #F2AA86 16.67%, #FFF 54.28%, #BD7810 91.9%)',
                gradient:
                    'linear-gradient(132deg, #F2AA8680 16.67%, #FFF80 54.28%, #BD781080 91.9%)',
            },
            {
                name: 'Ottoman',
                color: 'linear-gradient(132deg, #AEAA96 16.67%, #FFF 54.28%, #7E4D05 91.9%)',
                gradient:
                    'linear-gradient(132deg, #AEAA9680 16.67%, #FFF80 54.28%, #7E4D0580 91.9%)',
            },
        ],
    },
    {
        title: 'Radio-age series',
        effects: [
            {
                name: 'Static',
                color: 'linear-gradient(135deg, #FBF7F2 0%, #928F8F 100%)',
                gradient: 'linear-gradient(135deg, #FBF7F220 0%, #928F8F50 100%)',
            },
            {
                name: 'Frequency',
                color: 'linear-gradient(135deg, #191818 0%, #FFFDFD 100%)',
                gradient: 'linear-gradient(135deg, #19181840 0%, #FFFDFD40 100%)',
            },
            {
                name: 'Broadcast',
                color: 'linear-gradient(135deg, #6D6B67 0%, #444141 100%)',
                gradient: 'linear-gradient(135deg, #6D6B6740 0%, #44414140 100%)',
            },
            {
                name: 'Retro',
                color: 'linear-gradient(135deg, #6D6B67 0%, #444141 100%)',
                gradient: 'linear-gradient(135deg, #6D6B6740 0%, #44414140 100%)',
            },
            {
                name: 'Transistor',
                color: 'linear-gradient(135deg, #131311 0%, #1A1717 100%)',
                gradient: 'linear-gradient(135deg, #13131140 0%, #1A171740 100%)',
            },
            {
                name: 'Jazz',
                color: 'linear-gradient(132deg, #5C5C5C 16.67%, #FFF 54.28%, #191002 91.9%)',
                gradient:
                    'linear-gradient(132deg, #5C5C5C40 16.67%, #FFF40 54.28%, #19100240 91.9%)',
            },
            {
                name: 'Classic',
                color: 'linear-gradient(132deg, #B0B0B0 38.73%, rgba(255, 255, 255, 0.50) 54.28%, #000 77.07%)',
                gradient:
                    'linear-gradient(132deg, #B0B0B040 38.73%, rgba(255, 255, 255, 0.50) 54.28%, #000 77.07%)',
            },
            {
                name: 'Night8',
                color: 'linear-gradient(132deg, #FFF 34.39%, #000 54.28%, #FFF 72.37%)',
                gradient: 'linear-gradient(132deg, #FFF40 34.39%, #00040 54.28%, #FFF40 72.37%)',
            },
        ],
    },
];

const EffectsBlock: React.FC = () => {
    const dispatch = useAppDispatch();
    const selectedImage = useSelector(selectSelectedImage);
    const [activeEffect, setActiveEffect] = useState<string | null>(null);
    const [intensity, setIntensity] = useState(100);
    const [activeTab, setActiveTab] = useState(SERIES[0]?.title ?? '');

    useEffect(() => {
        if (selectedImage?.image?.visual_effects) {
            setActiveEffect(
                selectedImage.image?.visual_effects.name
                    ? SERIES.flatMap((s) => s.effects).find(
                          (e) =>
                              e.name.toLowerCase() ===
                              selectedImage.image?.visual_effects.name.toLowerCase(),
                      )?.name || null
                    : null,
            );

            setIntensity(
                selectedImage.image?.visual_effects.intensity != 0
                    ? selectedImage.image?.visual_effects.intensity
                    : 100,
            );
        } else {
            setActiveEffect(null);
            setIntensity(100);
        }
    }, [selectedImage]);

    if (!selectedImage?.image) return <p className={s.text}>No image selected</p>;
    const handleEffectClick = (eff: EffectDef, isNoIntensity: boolean) => {
        setActiveEffect(eff.name);

        dispatch(
            updateSelectedImageSetting({
                key: 'visual_effects' as any,
                value: {
                    name: eff.name.toLowerCase(),
                    intensity: eff.gradient ? 100 : intensity,
                },
            }),
        );

        dispatch(
            updateSelectedImageSetting({
                key: 'canvas_color' as any,
                value: {
                    ...selectedImage?.image?.canvas_color,
                    gradient: eff.gradient || 'none',
                },
            }),
        );
        dispatch(commitHistory({ label: 'effect:set' }));
    };

    const handleIntensityChange = (value: number) => {
        setIntensity(value);
        dispatch(
            updateSelectedImageSetting({
                key: 'visual_effects',
                value: {
                    name: activeEffect?.toLowerCase(),
                    intensity: value,
                },
            }),
        );
        dispatch(
            commitHistory({
                label: 'effect:intensity',
                opts: { groupId: 'effect:intensity', replace: true },
            }),
        );
    };

    return (
        <>
            <div className={s.container_desktop}>
                <p className={s.title}>Choose the effect on an image selected</p>

                {SERIES.map((series) => {
                    const isSeriesActive =
                        activeEffect !== null &&
                        series.effects.some((eff) => eff.name === activeEffect);
                    const isNightSeries = series.title === 'Night series';
                    const isEnigmaSeries = series.title === 'Enigma series';
                    const isAncientSeries = series.title === 'Ancient series';
                    const isRadioAgeSeries = series.title === 'Radio-age series';
                    const isNoIntensity =
                        isNightSeries || isEnigmaSeries || isAncientSeries || isRadioAgeSeries;

                    return (
                        <div key={series.title} className={s.block_container}>
                            <p className={s.series_title}>{series.title}</p>

                            <div className={s.grid_four_column_container}>
                                {series.effects.map((eff) => (
                                    <div
                                        key={eff.name}
                                        className={`${s.card} ${
                                            activeEffect === eff.name ? s.active : ''
                                        }`}
                                        onClick={() => {
                                            if (isNoIntensity) {
                                                setIntensity(100);
                                            }
                                            handleEffectClick(eff, isNoIntensity);
                                        }}
                                        style={{ width: '55px', height: '55px' }}
                                    >
                                        <div className={s.dot} style={{ background: eff.color }} />
                                        <p className={s.effect_name}>{eff.name}</p>
                                    </div>
                                ))}
                            </div>

                            {isSeriesActive && !isNoIntensity && (
                                <div
                                    className={s.slider_item}
                                    style={{ width: '80%', margin: '0 auto' }}
                                >
                                    <div
                                        className={s.label_wrapper}
                                        style={{ display: 'flex', justifyContent: 'center' }}
                                    >
                                        <label className={s.title} style={{ textAlign: 'center' }}>
                                            Effect intensity
                                        </label>
                                    </div>
                                    <Slider
                                        min={0}
                                        max={100}
                                        value={intensity}
                                        onChange={handleIntensityChange}
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className={s.mobile_container}>
                <div className={s.effect_scroll}>
                    {SERIES.find((s) => s.title === activeTab)?.effects.map((eff) => (
                        <div
                            key={eff.name}
                            className={`${s.card} ${activeEffect === eff.name ? s.active : ''}`}
                            onClick={() => {
                                const isNoIntensity = [
                                    'Night series',
                                    'Enigma series',
                                    'Ancient series',
                                    'Radio-age series',
                                ].includes(activeTab);
                                if (isNoIntensity) {
                                    setIntensity(100);
                                }
                                handleEffectClick(eff, isNoIntensity);
                            }}
                        >
                            <div className={s.dot} style={{ background: eff.color }} />
                            <p className={s.effect_name}>{eff.name}</p>
                        </div>
                    ))}
                </div>

                <div className={s.tabs_scroll}>
                    <div className={s.tabs}>
                        {SERIES.map((series) => (
                            <button
                                key={series.title}
                                className={`${s.tab} ${activeTab === series.title ? s.active_tab : ''}`}
                                onClick={() => setActiveTab(series.title)}
                            >
                                {series.title.replace(' series', '')}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <Slider min={0} max={100} value={intensity} onChange={handleIntensityChange} />
                    <div
                        className={s.label_wrapper}
                        style={{ display: 'flex', justifyContent: 'center' }}
                    >
                        <label className={s.title} style={{ textAlign: 'center' }}>
                            Effect intensity
                        </label>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EffectsBlock;
